import React, { Component } from 'react';
import { StatePlugin } from '../../capacitor-connector';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape, defineMessages, injectIntl } from 'react-intl';

import * as Shapes from '../../shapes';

import CapacitorRipple from '../capacitor/Ripple';
import LivestreamEventRelativeTime from './LivestreamEventRelativeTime';
import CurrentlyLive from './CurrentlyLive';
import AvailabilityInfo from '../livestream-event-preview/AvailabilityInfo';
import CoursePeriod from '../livestream-event-preview/CoursePeriod';
import EventPrice from '../livestream-event-preview/Price';
import Price from '../common/Price';
import Tags from '../recording/RecordingTags';
import PriceInfoTooltip from '../livestream-event/PriceInfoTooltip';

import livestreamEventComponent from '../../hoc/livestreamEventComponent';

import styles from './LivestreamEventHero.css';
import { PLAN_ID_PPC_12M, SUBSCRIPTION_PLANS } from '../../constants';
import ReactTooltip from 'react-tooltip';
import AccountJoin from '../messages/AccountJoin';
import GetPremiumButton from '../common/GetPremiumButton';

const messages = defineMessages({
  accessAllConcerts: {
    id: 'livestream-events.cta.access-all-concerts',
    defaultMessage: 'Access all concerts',
  },
});
class LivestreamEventHero extends Component {
  static propTypes = {
    event: Shapes.LivestreamEvent,
    renderHeroMedia: PropTypes.func.isRequired,
    onClick: PropTypes.func,
    onSubscribeClick: PropTypes.func.isRequired,
    onSignupClick: PropTypes.func.isRequired,

    userHasTicket: PropTypes.bool.isRequired,
    userHasAccessViaPPC: PropTypes.bool.isRequired,
    userCanAccessSubscribeView: PropTypes.bool.isRequired,

    ticketPurchaseIsDisabled: PropTypes.bool.isRequired,
    shouldShowPrice: PropTypes.bool.isRequired,

    isAuth: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
  };

  state = {
    capacitorTicketPurchaseIsEnabled: null,
  };

  async componentDidMount() {
    if (__CAPACITOR__) {
      const webTicketPurchaseState = await StatePlugin.androidShouldSendCloudConfigItem({
        cloudConfigKey: 'WebTicketPurchaseEnabled',
      });

      if (
        webTicketPurchaseState &&
        typeof webTicketPurchaseState.cloudConfigItemValue === 'string'
      ) {
        // We want to make sure that if the toggle is not set or set to a
        // non-boolean value, we default to true
        const isWebTicketPurchaseEnabled = webTicketPurchaseState.cloudConfigItemValue !== 'false';
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({
          capacitorTicketPurchaseIsEnabled: isWebTicketPurchaseEnabled,
        });
      } else {
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({
          capacitorTicketPurchaseIsEnabled: true,
        });
      }
    }
  }

  onClick = () => {
    const { onClick, event } = this.props;
    if (onClick) {
      onClick(event);
    }
  };

  renderDefaultTime() {
    const { event } = this.props;

    return (
      <React.Fragment>
        {event.isLive ? (
          <CurrentlyLive />
        ) : (
          <div className="live-timestamp">
            <LivestreamEventRelativeTime event={event} />
          </div>
        )}
      </React.Fragment>
    );
  }

  renderTime() {
    const { event } = this.props;

    if (event.isConcert) {
      return <AvailabilityInfo event={event} showUpcomingTime />;
    }

    if (event.isCourse) {
      return <CoursePeriod event={event} />;
    }

    return this.renderDefaultTime();
  }

  renderPrice = () => {
    const { price } = this.props.event;
    const { isFree, isFixed } = price;

    return (
      <div className={styles.price} data-test="livestream-event-hero.price">
        <EventPrice price={price} />
        {!isFree && !isFixed && <PriceInfoTooltip />}
      </div>
    );
  };

  renderAccessFreeEventCTA = () => {
    const { isAuth } = this.props;
    return (
      <React.Fragment>
        {this.renderSignupCTA()}
        {this.renderSubscribeCTA(isAuth ? 'c-btn--is-purple' : '')}
      </React.Fragment>
    );
  };

  renderExclusiveToPPC = () => {
    return (
      <div>
        <p className={styles.info}>
          <FormattedMessage
            id="livestream-events.featured-event.exclusive-to-ppc-subscribers-text"
            defaultMessage="Exclusive to Premium Audio + Video subscribers"
          />
        </p>
        {this.renderSubscribeCTA('c-btn--is-purple')}
      </div>
    );
  };

  renderSignupCTA = () => {
    const { onSignupClick, isAuth } = this.props;

    if (isAuth) {
      return null;
    }

    const id = 'create-account';

    return (
      <span>
        <button
          className={`c-btn c-btn--is-purple ${styles.ctaButton}`}
          data-tip
          data-for={id}
          onClick={onSignupClick}
          data-test="livestream-event-hero.create-account-btn"
        >
          <CapacitorRipple />
          <AccountJoin />
        </button>
        <ReactTooltip className={styles.tooltip} id={id} effect="solid" place="top">
          <FormattedMessage
            id="livestream-events.create-account-tooltip.text"
            defaultMessage="This event is free. Simply log in or create your free IDAGIO account to watch."
          />
        </ReactTooltip>
      </span>
    );
  };

  renderSubscribeCTA = className => {
    const { onSubscribeClick, userCanAccessSubscribeView, isAuth } = this.props;

    if (!userCanAccessSubscribeView) {
      return null;
    }

    const classNames = `c-btn ${styles.ctaButton} ${className || ''}`;

    if (isAuth) {
      return (
        <GetPremiumButton
          className={classNames}
          trackingContext={{ trigger: 'livestreamEvent' }}
          targetPlan={PLAN_ID_PPC_12M}
          data-test="livestream-event-hero.access-all-concerts-btn"
          text={this.props.intl.formatMessage(messages.accessAllConcerts)}
        />
      );
    }

    const id = 'access-all-concerts';

    return (
      <span>
        <button
          className={classNames}
          data-tip
          data-for={id}
          onClick={onSubscribeClick}
          data-test="livestream-event-hero.access-all-concerts-btn"
        >
          <CapacitorRipple />
          <FormattedMessage
            id="livestream-events.cta.access-all-concerts"
            defaultMessage="Access all concerts"
          />
        </button>
        <ReactTooltip className={styles.tooltip} id={id} effect="solid" place="top">
          <FormattedMessage
            id="livestream-events.access-all-concerts-tooltip.text"
            defaultMessage="Get unlimited access to all concerts for just {price}/month"
            values={{
              price: <Price price={SUBSCRIPTION_PLANS[PLAN_ID_PPC_12M].monthlyPrice} />,
            }}
          />
        </ReactTooltip>
      </span>
    );
  };

  renderPurchasePaidEventCTA = () => {
    const { event, ticketPurchaseIsDisabled } = this.props;

    if (__CAPACITOR__ && this.state.capacitorTicketPurchaseIsEnabled === null) {
      // We're loading
      return null;
    }

    const shouldDisableTicketPurchase =
      ticketPurchaseIsDisabled || (__CAPACITOR__ && !this.state.capacitorTicketPurchaseIsEnabled);

    if (event.isIncludedInPPC) {
      return shouldDisableTicketPurchase
        ? this.renderExclusiveToPPC()
        : this.renderSubscribeCTA('c-btn--is-purple');
    }

    return null;
  };

  renderIsBooked = () => {
    return (
      <div>
        <div className={styles.isBooked} data-test="livestream-event-hero.booked-tag">
          <FormattedMessage
            id="livestream-events.featured-event.event-booked"
            defaultMessage="Booked"
          />
        </div>
        {this.props.event.isUpcoming && (
          <p className={styles.info} data-test="livestream-event-hero.booked-info">
            <FormattedMessage
              id="livestream-events.featured-event.event-booked-info"
              defaultMessage="Please return to this page at the start of the event"
            />
          </p>
        )}
      </div>
    );
  };

  renderCTAs = () => {
    if (!this.props.event.isPaidEvent && this.props.event.isAvailable) {
      return this.renderAccessFreeEventCTA();
    }

    if (this.props.userHasTicket) {
      return this.renderIsBooked();
    }

    if (this.props.event.areTicketsAvailable && !this.props.userHasAccessViaPPC) {
      return this.renderPurchasePaidEventCTA();
    }

    return null;
  };

  render() {
    const { event, renderHeroMedia, shouldShowPrice } = this.props;

    return (
      <React.Fragment>
        {__CAPACITOR__ && <h2>{event.title}</h2>}
        {renderHeroMedia()}

        <div className={styles.ctasWrapper}>{this.renderCTAs()}</div>

        <div className={styles.eventInfo}>
          <div className={styles.time}>{this.renderTime()}</div>
          {shouldShowPrice && this.renderPrice()}
          {event.tags && <Tags tags={event.tags} />}
        </div>
      </React.Fragment>
    );
  }
}

export default injectIntl(livestreamEventComponent()(LivestreamEventHero));
